/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState } from 'react';
import envConfig from '../config/envConfig';
import GaneshaLogo from './GaneshaLogo';
import { useTheme } from '../theme/ThemeContext';
import Button from './Button';
import { useNavigate } from 'react-router-dom';
import { retrieve } from '../utils/storageHelper';
import { isUserLoggedIn } from '../utils/utils';

function Banner() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      const result = await isUserLoggedIn();
      if (!mounted) return;
      setIsLoggedIn(result);
    })();

    return () => { mounted = false; };
  }, []);
  const userData = isLoggedIn ? retrieve('userData') : {};
  const userName = userData?.user_name || 'User';

  const handleSignInClick = () => {
    navigate('/sign-in');
  };

  const handleSignUpClick = () => {
    navigate('/sign-up')
  };

  const handleScrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      className="banner d-flex align-items-center justify-content-center py-5"
      style={{
        background: '#fff',
        minHeight: '600px', // Increased height for better hero presence
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <div className="container position-relative z-1">
        <div className="row align-items-center">
          <div className="col-lg-7 text-center text-lg-start mb-5 mb-lg-0">
            {/* Logo is optional in Hero if present in Header, but keeping consistent with request if needed. 
                However, arcee.io usually has it in header. 
                Let's keep text focus as per redesign plan. */}

            {/* Enterprise Ready Widget */}
            <div className="d-inline-flex align-items-center px-3 py-2 mb-4 shadow-sm"
              style={{
                background: '#fff',
                border: `1px solid ${theme.primary}25`,
                borderRadius: '6px'
              }}>
              <div className="me-2" style={{ width: 10, height: 10, background: theme.primary, borderRadius: 2 }}></div>
              <span style={{ color: theme.primary, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                Enterprise Ready
              </span>
            </div>

            <h1 className="fw-bold mb-3 display-3" style={{ lineHeight: 1.1 }}>
              <span style={{
                background: theme.gradientBackground,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Build Bigger.</span><br />
              <span style={{
                background: theme.gradientBackground,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                opacity: 0.9
              }}>Ship Faster.</span>
            </h1>
            <p className="lead mb-4 fs-4 text-muted" style={{ maxWidth: '600px', margin: '0 auto 0 0' }}>
              {envConfig.appName} empowers enterprises to launch complex, scalable
              applications in days. Get the agility of No-Code with the raw power of a
              Headless Django Backend.
            </p>
            <p className="lead mb-4 fs-4 text-muted" style={{ maxWidth: '600px', margin: '0 auto 0 0' }}>
              Please feel free to contact us for custom needs or AI enabled rapid application development.
            </p>
            <div className='d-flex flex-wrap gap-3 justify-content-center justify-content-lg-start'>
              {isLoggedIn ? (
                <>
                  <div className='d-flex align-items-center text-dark me-3'>
                    <i className="bi bi-person-circle fs-4 me-2"></i>
                    <span className='fs-5'>Hi, {userName}</span>
                  </div>
                  <Button mode="primary" onClick={() => navigate('/dashboard')} className="px-4 py-2 fs-5">
                    Go to Dashboard <i className="bi bi-arrow-right ms-2"></i>
                  </Button>
                </>
              ) : (
                <>
                  <Button mode="primary" className="px-5 py-3 fs-5 fw-bold shadow-lg" onClick={() => handleScrollTo('contact')}>
                    Request Demo
                  </Button>
                  <Button mode="secondary" className="px-5 py-3 fs-5 fw-bold" onClick={() => handleScrollTo('about')}>
                    See Details
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="col-lg-5 d-none d-lg-block fade-in-up">
            <div style={{
              background: '#fff',
              borderRadius: theme.cardRadius,
              border: `1px solid ${theme.secondary}40`,
              padding: '2rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
            }}>
              <div className="d-flex gap-2 mb-3">
                <div className="rounded-circle bg-danger" style={{ width: 12, height: 12 }}></div>
                <div className="rounded-circle bg-warning" style={{ width: 12, height: 12 }}></div>
                <div className="rounded-circle bg-success" style={{ width: 12, height: 12 }}></div>
              </div>
              <div style={{ fontFamily: 'monospace', color: '#333', fontSize: '0.9rem' }}>
                <div className="mb-2"><span className="text-muted"># Define your data model</span></div>
                <div><span className="text-secondary fw-bold">class</span> <span className="text-dark fw-bold">Order</span>(Model):</div>
                <div className="ps-3"><span className="text-primary">customer</span> = Link(<span className="text-success">"Customer"</span>)</div>
                <div className="ps-3"><span className="text-primary">total</span> = Currency(min=<span className="text-warning">0</span>)</div>
                <div className="ps-3"><span className="text-primary">status</span> = Select([<span className="text-success">"Pending"</span>, <span className="text-success">"Shipped"</span>])</div>
                <br />
                <div className="mb-2"><span className="text-muted"># APIs generated automatically</span></div>
                <div className="mb-2"><span className="text-muted"># Admin UI generated instantly</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Abstract Background Shapes - lighter for white bg */}
      <div style={{
        position: 'absolute',
        top: '-10%',
        right: '-5%',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: `radial-gradient(circle, ${theme.secondary}10 0%, rgba(255,255,255,0) 70%)`,
        zIndex: 0
      }}></div>
    </section>
  );
}

export default Banner;
